// components
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import { Icon } from '@sdb/planning-ui';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import {
  ConsequenceCell,
  ConsequenceHeaderRow,
  ConsequenceRow,
  ConsequencesBox,
  ErrorBox,
  FlagCell,
  HintText,
  LoaderSlot,
  MutationChip,
  PlanUnitChevron,
  PlanUnitGroupBox,
  PlanUnitGroupHeader,
  PlanUnitIcon,
  PlanUnitMeta,
  PlanUnitWarningBox,
  Refreshing,
  ShowAllButton,
  SummaryRow,
  WarningBox
} from './RegisterAbsenceDialog.styles';

// utils
import { format } from 'date-fns';
import { formatByLocale, parseBackendDateTime } from 'utils/date';
import {
  givesOpenDuty,
  groupConsequences,
  noOpenDutyReason,
  summarize
} from './ConsequencesPanel.utils';

// types
import type {
  AbsenceRolloutDutyLine,
  AbsenceRolloutErrorDutyLine
} from 'RestAPIs/roster/register-absence';
import type {
  ConsequenceKind,
  ConsequenceRowModel,
  ConsequencesPanelProps,
  MutationTone,
  NoOpenDutyReason,
  PlanUnitGroup
} from './ConsequencesPanel.types';

/**
 * How long the loading bar stays visible after a calculation finished. The
 * rollout often answers within a few dozen milliseconds; without this tail the
 * bar blinks once and is gone before it reads as "this is being recalculated".
 */
const LOADER_TAIL_MS = 700;

/**
 * A plan unit with more duties than this starts with only the first
 * `FOLDED_ROWS` of them, so the other plan units are still in view without
 * scrolling; "Toon alle … diensten" shows the rest.
 */
const FOLD_ABOVE_ROWS = 5;
const FOLDED_ROWS = 3;

const TONE_BY_KIND: Record<ConsequenceKind, MutationTone> = {
  modified: 'converted',
  detached: 'detached',
  new: 'created',
  unchanged: 'none',
  covered: 'none',
  untouched: 'none'
};

/** the tooltip of each "Nee" in the Ontkoppeld column */
const NO_OPEN_DUTY_KEY: Record<NoOpenDutyReason, string> = {
  noRequest: 'reactRegisterAbsence.noOpenDutyNoRequestTooltip',
  typeDoesNotDetach: 'reactRegisterAbsence.noOpenDutyTypeTooltip',
  newRegistration: 'reactRegisterAbsence.noOpenDutyNewTooltip',
  unchanged: 'reactRegisterAbsence.noOpenDutyUnchangedTooltip',
  covered: 'reactRegisterAbsence.noOpenDutyCoveredTooltip',
  untouched: 'reactRegisterAbsence.noOpenDutyUntouchedTooltip'
};

/** weekday and date from the same field: they can never contradict each
 *  other */
const formatWeekday = (line: AbsenceRolloutDutyLine) =>
  formatByLocale(parseBackendDateTime(line.startTime), 'EEEEEE');

const formatDate = (line: AbsenceRolloutDutyLine) =>
  formatByLocale(parseBackendDateTime(line.startTime), 'd MMM');

const formatTime = (line: AbsenceRolloutDutyLine) =>
  `${format(parseBackendDateTime(line.startTime), 'HH:mm')}–${format(
    parseBackendDateTime(line.endTime),
    'HH:mm'
  )}`;

/**
 * Live consequences of the absence for the planning, grouped the way the
 * planner reads them: per plan unit the employee was planned on (or not),
 * then per day. Every row says on which work agreement the duty is, what
 * happens to it, whether a request is linked and whether an open duty comes
 * back — the last one only happens with a request to detach from.
 */
const ConsequencesPanel = ({
  existingDutyLines,
  newDutyLines,
  selectedWaIds,
  workAgreements = [],
  hasSelection,
  isLoading,
  hasResult,
  dutyHourTypeDescription,
  detachedGivesOpenDuty,
  rolloutErrorKey,
  errorDutyLines = [],
  overMaxHoursPerDay,
  coveredPeriods = [],
  isEdit
}: ConsequencesPanelProps) => {
  const { t, i18n } = useTranslation('common');

  // a refused rollout explains itself; an unknown key falls back to a
  // generic "could not calculate" instead of claiming nothing changes
  const rolloutErrorText = rolloutErrorKey
    ? i18n.exists(rolloutErrorKey)
      ? t(rolloutErrorKey)
      : t('reactRegisterAbsence.rolloutFailed')
    : undefined;

  const openDutyOptions = { detachedGivesOpenDuty, isEdit };
  const groups = groupConsequences({
    existingDutyLines,
    newDutyLines,
    selectedWaIds,
    workAgreements,
    coveredPeriods,
    isEdit
  });
  const summary = summarize(groups, openDutyOptions);

  const workAgreementOf = (line: AbsenceRolloutDutyLine) =>
    workAgreements.find((workAgreement) => workAgreement.waId === line.waId);

  const workAgreementNr = (line: AbsenceRolloutDutyLine) =>
    workAgreementOf(line)?.nr ?? line.waNr ?? '';

  /** the column is narrow: number and function; the tooltip adds the plan
   *  unit the work agreement hangs on */
  // number 0 is a real work agreement number: filter on "absent", not on
  // falsy, or the first work agreement of some employees loses its number
  const workAgreementLabel = (line: AbsenceRolloutDutyLine) =>
    [workAgreementNr(line), workAgreementOf(line)?.functionDescription]
      .filter((part) => part !== undefined && part !== null && part !== '')
      .join(' · ');

  const workAgreementTooltip = (line: AbsenceRolloutDutyLine) =>
    [
      t('reactRegisterAbsence.workAgreementShort', {
        nr: workAgreementNr(line)
      }),
      workAgreementOf(line)?.functionDescription,
      workAgreementOf(line)?.planUnit
    ]
      .filter(Boolean)
      .join(' · ');

  /**
   * The open-duty outcome says the original duty comes back to be refilled.
   * Where it comes back matters as much as that it does, so it names the plan
   * unit the duty was planned on.
   */
  const openDutyTooltip = (line: AbsenceRolloutDutyLine) => {
    // which duty comes back, in the planner's terms: code and times. Without
    // a code the times alone read as belonging to another row, so the duty is
    // then not described at all
    // while editing, a line without a code of its own carries the absence's
    // type name in its place ("Ziekte"): that is not the duty that comes back
    const code = isEdit ? undefined : line.displayDutyCode || line.dutyCode;
    const costCenter = line.costCenterFullName;
    if (!code) {
      return costCenter
        ? t('reactRegisterAbsence.openDutyNoCodeOnCostCenterTooltip', {
            costCenter
          })
        : t('reactRegisterAbsence.openDutyNoCodeTooltip');
    }
    const duty = `${code}, ${formatTime(line)}`;
    return costCenter
      ? t('reactRegisterAbsence.openDutyOnCostCenterTooltip', {
          duty,
          costCenter
        })
      : t('reactRegisterAbsence.openDutyTooltip', { duty });
  };

  const noOpenDutyTooltip = (kind: ConsequenceKind) =>
    t(NO_OPEN_DUTY_KEY[noOpenDutyReason(kind, { detachedGivesOpenDuty })]);

  /** a refused day carries no times, only the date it was meant for */
  const errorDayLabel = (line: AbsenceRolloutErrorDutyLine) =>
    line.date
      ? formatByLocale(parseBackendDateTime(line.date), 'EEEEEE d MMM')
      : '';

  // same fallback as a refused rollout: an unknown key must not leave the day
  // standing there without a reason
  const errorReason = (line: AbsenceRolloutErrorDutyLine) =>
    line.error && i18n.exists(line.error)
      ? t(line.error)
      : t('reactRegisterAbsence.rolloutSkippedDayUnknownReason');

  /** what the row does, described; advice stays in the warnings above */
  const tooltipFor = ({ line, kind, covered }: ConsequenceRowModel) => {
    switch (kind) {
      case 'covered':
        return t('reactRegisterAbsence.actionCoveredTooltip', {
          from: covered ? formatByLocale(covered.start, 'd MMM') : '',
          to: covered ? formatByLocale(covered.end, 'd MMM') : ''
        });
      case 'modified': {
        const converted = t('reactRegisterAbsence.actionModifiedTooltip', {
          type: dutyHourTypeDescription
        });
        // while editing there is no Ontkoppeld column, yet a change to a
        // detaching type still unlinks the request: say it with the row
        return isEdit && givesOpenDuty(line, kind, openDutyOptions)
          ? `${converted} ${openDutyTooltip(line)}`
          : converted;
      }
      case 'detached':
        // shrinking an absence deletes the duty outright
        if (isEdit) {
          return t('reactRegisterAbsence.actionDetachedEditTooltip');
        }
        // without an underlying request there is nothing to unlink: the
        // duty is removed, whatever the duty hour type promises
        return detachedGivesOpenDuty && line.requestId
          ? t('reactRegisterAbsence.actionDetachedOpenDutyTooltip')
          : t('reactRegisterAbsence.actionDetachedTooltip');
      case 'new':
        return t('reactRegisterAbsence.actionNewTooltip', {
          type: dutyHourTypeDescription
        });
      case 'unchanged':
        return t(
          isEdit
            ? 'reactRegisterAbsence.actionUnchangedEditTooltip'
            : 'reactRegisterAbsence.actionUnchangedTooltip'
        );
      case 'untouched':
        return t('reactRegisterAbsence.actionUntouchedTooltip');
    }
  };

  /** the mutation as a label: `V107 → Ziekte`, `A23 · uitgeroosterd`. While
   *  editing, a conversion leaves the from-side out: it is the type already
   *  shown in the dropdown */
  const mutationText = ({ line, kind }: ConsequenceRowModel) => {
    // a registration without a planned duty (leave, sickness on a fixed
    // working day) has no duty code: it is named "Zonder dienstcode". While
    // editing, the line carries the absence's own duty hour type in its place
    // ("Ziekte · ongewijzigd"); when even that is unknown the label stands
    // alone, since every line belongs to the absence being edited
    const dutyCode = line.displayDutyCode || line.dutyCode || '';
    const name =
      dutyCode || (isEdit ? '' : t('reactRegisterAbsence.mutationNoDutyCode'));
    const withCode = (label: string) => (name ? `${name} · ${label}` : label);
    const target = dutyHourTypeDescription ?? '';

    switch (kind) {
      // without a code the from-side is left out: `→ Ziekte`; while editing
      // it is the type already in the dropdown
      case 'modified':
        return !isEdit && dutyCode ? `${dutyCode} → ${target}` : `→ ${target}`;
      case 'detached':
        return withCode(
          t(
            isEdit
              ? 'reactRegisterAbsence.mutationRemoved'
              : 'reactRegisterAbsence.mutationDetached'
          )
        );
      case 'new':
        return t('reactRegisterAbsence.mutationFixedWorkday', {
          type: dutyHourTypeDescription ?? ''
        });
      // the day already belongs to another absence
      case 'covered':
        return [dutyCode, t('reactRegisterAbsence.mutationCovered')]
          .filter(Boolean)
          .join(' · ');
      case 'unchanged':
        return withCode(t('reactRegisterAbsence.mutationUnchanged'));
      case 'untouched':
        return withCode(t('reactRegisterAbsence.mutationUntouched'));
    }
  };

  const groupMeta = (group: PlanUnitGroup) => {
    const nrs = (list: string[]) => list.join(', ');
    if (group.role === 'untouched') {
      return t('reactRegisterAbsence.planUnitUntouched', {
        nrs: nrs(group.ownNrs)
      });
    }
    return [
      group.ownNrs.length
        ? t('reactRegisterAbsence.planUnitOwn', { nrs: nrs(group.ownNrs) })
        : undefined,
      group.deployedNrs.length
        ? t('reactRegisterAbsence.planUnitDeployed', {
            nrs: nrs(group.deployedNrs)
          })
        : undefined,
      group.rows.length
        ? t('reactRegisterAbsence.planUnitDutyCount', {
            count: group.rows.length
          })
        : t('reactRegisterAbsence.planUnitNotPlanned')
    ]
      .filter(Boolean)
      .join(' · ');
  };

  /** every row answers: Ja (an open duty comes back) or Nee, with why not */
  const openDutyCell = (row: ConsequenceRowModel) => {
    const opens = givesOpenDuty(row.line, row.kind, openDutyOptions);
    const label = opens
      ? openDutyTooltip(row.line)
      : noOpenDutyTooltip(row.kind);

    // beside the answer, not below it: below, the bubble covers the next row
    // and reads as that row's duty
    return (
      <Tooltip title={label} arrow placement="left">
        <FlagCell positive={opens} aria-label={label}>
          {opens && <Icon type="check" fontSize="inherit" />}
          {t(
            opens
              ? 'reactRegisterAbsence.flagYes'
              : 'reactRegisterAbsence.flagNo'
          )}
        </FlagCell>
      </Tooltip>
    );
  };

  const summaryItems = (
    [
      {
        tone: 'converted',
        count: summary.modified,
        text: t('reactRegisterAbsence.summaryModified', {
          count: summary.modified,
          type: dutyHourTypeDescription ?? ''
        })
      },
      {
        tone: 'detached',
        count: summary.detached,
        text: t(
          isEdit
            ? 'reactRegisterAbsence.summaryRemoved'
            : 'reactRegisterAbsence.summaryDetached',
          { count: summary.detached }
        )
      },
      {
        tone: 'created',
        count: summary.created,
        text: t('reactRegisterAbsence.summaryCreated', {
          count: summary.created
        })
      },
      {
        tone: 'created',
        // while editing only a change to a detaching type gives any: the
        // count is then the one place besides the row tooltip that says so
        count: summary.openDuties,
        text: t('reactRegisterAbsence.summaryOpenDuties', {
          count: summary.openDuties
        })
      }
    ] satisfies { tone: MutationTone; count: number; text: string }[]
  ).filter((item) => item.count > 0);

  const selectedExisting = existingDutyLines.filter(
    (line) => !!line.waId && selectedWaIds.includes(line.waId)
  );

  // keep the rows visible while refreshing, so a changed date does not blank
  // the panel for a moment (react-query keeps the previous data)
  const isFirstLoad = isLoading && !existingDutyLines.length;
  const isEmpty =
    hasSelection && !selectedExisting.length && !newDutyLines.length;

  // folding per plan unit, keyed on the group: only what the planner changed
  // is stored, so a group that appears later gets its default. Folded in from
  // the start: unchecked work agreements (nothing changes there), and — with
  // more than one work agreement — plan units without duties, whose advice
  // would otherwise push the ones that do change out of view. Deliberately
  // not reset when the period changes: the plan unit is the same, and a block
  // the planner folded away should not spring open on every recalculation
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});
  const [showAll, setShowAll] = useState<Record<string, boolean>>({});
  const hasSeveralWorkAgreements = workAgreements.length > 1;
  const isOpen = (group: PlanUnitGroup) =>
    openOverride[group.key] ??
    (group.role !== 'untouched' &&
      !(hasSeveralWorkAgreements && group.rows.length === 0));
  const toggleOpen = (group: PlanUnitGroup) =>
    setOpenOverride((current) => ({ ...current, [group.key]: !isOpen(group) }));
  const toggleShowAll = (group: PlanUnitGroup) =>
    setShowAll((current) => ({ ...current, [group.key]: !current[group.key] }));
  const visibleRows = (group: PlanUnitGroup) =>
    group.rows.length > FOLD_ABOVE_ROWS && !showAll[group.key]
      ? group.rows.slice(0, FOLDED_ROWS)
      : group.rows;

  // starts visible (the dialog opens straight into a calculation) and keeps
  // running for a moment after each answer; a recalculation that starts within
  // that moment cancels the hide, so the bar does not stutter while typing
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    if (isLoading) {
      setShowLoader(true);
      return;
    }
    const timer = setTimeout(() => setShowLoader(false), LOADER_TAIL_MS);

    return () => clearTimeout(timer);
  }, [isLoading]);

  /**
   * Both warnings below say some form of "nothing is going to happen here", so
   * they are judged the same way, and asymmetrically:
   *
   * - claiming it waits for a settled result — never on the emptiness before
   *   the first calculation arrived, and never while a recalculation runs, or
   *   the warning would disappear and flash back;
   * - withdrawing it happens the moment duties are on screen. Waiting for the
   *   loader tail there left "there are no duties to change" standing next to
   *   the very duties that disproved it.
   */
  const settled = hasResult && !showLoader;

  const [settledEmpty, setSettledEmpty] = useState(false);
  useEffect(() => {
    if (!isEmpty) {
      setSettledEmpty(false);
      return;
    }
    if (settled) {
      setSettledEmpty(true);
    }
  }, [settled, isEmpty]);

  const [settledOverMaxHours, setSettledOverMaxHours] = useState(false);
  useEffect(() => {
    if (!isEmpty) {
      setSettledOverMaxHours(false);
      return;
    }
    if (settled) {
      setSettledOverMaxHours(!!overMaxHoursPerDay);
    }
  }, [settled, isEmpty, overMaxHoursPerDay]);

  return (
    <ConsequencesBox data-testid="consequences-panel">
      {/* the panel recalculates on every change, so the bar keeps its 4px slot
          whether or not it is visible: no rows jumping up and down */}
      <LoaderSlot>
        {showLoader && (
          <LinearProgress
            data-testid="consequences-loader"
            sx={{ height: 4, borderRadius: 2 }}
          />
        )}
      </LoaderSlot>
      {hasResult && !showLoader && !hasSelection && (
        <HintText>
          {t('reactRegisterAbsence.consequencesSelectWorkAgreement')}
        </HintText>
      )}
      {rolloutErrorText && (
        <ErrorBox sx={{ mb: 1 }}>{rolloutErrorText}</ErrorBox>
      )}
      {/* over the legal maximum no rollout is asked for at all, so the panel
          says that instead of letting the empty result read as "nothing to
          do here" */}
      {!rolloutErrorText && settledOverMaxHours && (
        <WarningBox sx={{ mb: 1, opacity: isLoading ? 0.6 : 1 }}>
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('reactRegisterAbsence.rolloutOverMaxHoursWarning')}
          </Box>
          {t('reactRegisterAbsence.rolloutOverMaxHoursTip')}
        </WarningBox>
      )}
      {/* editing has no work agreement checkbox and no rollout, so the
          register-mode advice would be nonsense there: an absence without
          linked duties simply has none to change */}
      {!rolloutErrorText &&
        !settledOverMaxHours &&
        settledEmpty &&
        hasSelection && (
          <WarningBox sx={{ mb: 1, opacity: isLoading ? 0.6 : 1 }}>
            {/* registering says it in the tip alone: a headline that
                "nothing changes" only repeated it */}
            {isEdit && (
              <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                {t('reactRegisterAbsence.editNoDutiesWarning')}
              </Box>
            )}
            {t(
              isEdit
                ? 'reactRegisterAbsence.editNoDutiesTip'
                : 'reactRegisterAbsence.consequencesNoneTip'
            )}
          </WarningBox>
        )}
      {!isFirstLoad && groups.length > 0 && (
        <Refreshing refreshing={isLoading}>
          {summaryItems.length > 0 && (
            <SummaryRow data-testid="consequences-summary">
              {summaryItems.map((item) => (
                <MutationChip key={item.text} tone={item.tone}>
                  {item.text}
                </MutationChip>
              ))}
            </SummaryRow>
          )}
          {groups.map((group) => {
            const open = isOpen(group);
            // a plan unit without duties still folds: it carries the
            // "nothing to change" advice for its work agreement
            const isGroupEmpty = group.rows.length === 0;
            // when nothing at all changes for the ticked work agreements, the
            // panel already says so above the table: not a second time here
            const showsAdvice = group.showsNoDutiesAdvice && !isEmpty;
            const foldable = !isGroupEmpty || showsAdvice;
            return (
              <PlanUnitGroupBox
                key={group.key}
                data-testid="plan-unit-group"
                data-role={group.role}
                data-open={foldable && open}
              >
                <PlanUnitGroupHeader
                  type="button"
                  muted={group.role === 'untouched'}
                  disabled={!foldable}
                  aria-expanded={foldable ? open : undefined}
                  onClick={() => toggleOpen(group)}
                >
                  {foldable && (
                    <PlanUnitChevron open={open}>
                      <Icon type="chevron-down" fontSize="inherit" />
                    </PlanUnitChevron>
                  )}
                  <PlanUnitIcon>
                    <Icon type="building" fontSize="inherit" />
                  </PlanUnitIcon>
                  <span>
                    {group.planUnit ||
                      t('reactRegisterAbsence.planUnitUnknown')}
                  </span>
                  <PlanUnitMeta>{groupMeta(group)}</PlanUnitMeta>
                </PlanUnitGroupHeader>
                {/* column labels per plan unit: after scrolling past the
                    first block the next one still says what is what */}
                {open && showsAdvice && (
                  <PlanUnitWarningBox>
                    {t(
                      isEdit
                        ? 'reactRegisterAbsence.editNoDutiesTip'
                        : 'reactRegisterAbsence.consequencesNoneTip'
                    )}
                  </PlanUnitWarningBox>
                )}
                {foldable && open && !isGroupEmpty && (
                  <>
                    <ConsequenceHeaderRow withDetaches={!isEdit}>
                      <span>{t('reactRegisterAbsence.colDay')}</span>
                      <span>{t('reactRegisterAbsence.colDate')}</span>
                      <span>{t('reactRegisterAbsence.colTime')}</span>
                      <span>
                        {t('reactRegisterAbsence.colWorkAgreementShort')}
                      </span>
                      <span>{t('reactRegisterAbsence.colMutation')}</span>
                      {!isEdit && (
                        <span>{t('reactRegisterAbsence.colDetaches')}</span>
                      )}
                    </ConsequenceHeaderRow>
                    {visibleRows(group).map((row) => (
                      <ConsequenceRow
                        key={row.key}
                        data-testid="consequence-row"
                        data-kind={row.kind}
                        shaded={row.shaded}
                        withDetaches={!isEdit}
                        // greyed out: not part of the save (untouched), owned
                        // by an absence that is already registered (covered),
                        // or taken off the employee (detached: unscheduled, or
                        // removed while editing). The Ontkoppeld answer keeps
                        // its colour, because that is what still happens
                        dimmed={
                          row.kind === 'untouched' ||
                          row.kind === 'covered' ||
                          row.kind === 'detached'
                        }
                      >
                        <ConsequenceCell>
                          {formatWeekday(row.line)}
                        </ConsequenceCell>
                        <ConsequenceCell>
                          {formatDate(row.line)}
                        </ConsequenceCell>
                        <ConsequenceCell>
                          {formatTime(row.line)}
                        </ConsequenceCell>
                        <Tooltip title={workAgreementTooltip(row.line)} arrow>
                          <ConsequenceCell>
                            {workAgreementLabel(row.line)}
                          </ConsequenceCell>
                        </Tooltip>
                        <ConsequenceCell>
                          <Tooltip title={tooltipFor(row)} arrow>
                            <MutationChip
                              tone={TONE_BY_KIND[row.kind]}
                              muted={row.kind === 'detached' && !isEdit}
                            >
                              {mutationText(row)}
                            </MutationChip>
                          </Tooltip>
                        </ConsequenceCell>
                        {/* while editing the column is left out (a user
                            decision); a type change that still unlinks is
                            told by the summary and the row tooltip */}
                        {!isEdit && openDutyCell(row)}
                      </ConsequenceRow>
                    ))}
                    {group.rows.length > FOLD_ABOVE_ROWS && (
                      <ShowAllButton
                        type="button"
                        onClick={() => toggleShowAll(group)}
                      >
                        {showAll[group.key]
                          ? t('reactRegisterAbsence.showFewerDuties')
                          : t('reactRegisterAbsence.showAllDuties', {
                              count: group.rows.length
                            })}
                      </ShowAllButton>
                    )}
                  </>
                )}
              </PlanUnitGroupBox>
            );
          })}
        </Refreshing>
      )}
      {/* the rollout answers a refused day with a reason instead of a duty;
          dropping those lines would quietly shorten the list */}
      {!isFirstLoad && errorDutyLines.length > 0 && (
        <WarningBox sx={{ mt: 1, opacity: isLoading ? 0.6 : 1 }}>
          <Box sx={{ fontWeight: 600, mb: 0.5 }}>
            {t('reactRegisterAbsence.rolloutSkippedDaysWarning')}
          </Box>
          {errorDutyLines.map((line, index) => (
            <Box key={`${line.waId}-${line.date ?? index}`}>
              {[errorDayLabel(line), errorReason(line)]
                .filter(Boolean)
                .join(' — ')}
            </Box>
          ))}
        </WarningBox>
      )}
    </ConsequencesBox>
  );
};

export default ConsequencesPanel;
